/** Change Order billing / invoice fields (jphph_change_orders). */

import { supabase } from '@/lib/supabase';

export type ChangeOrderInvoiceStatus =
  | 'not_invoiced'
  | 'invoiced'
  | 'paid'
  | 'cancelled'
  | 'not_billable';

export const CO_INVOICE_STATUSES: ChangeOrderInvoiceStatus[] = [
  'not_invoiced',
  'invoiced',
  'paid',
  'cancelled',
  'not_billable',
];

export const CO_INVOICE_STATUS_LABELS: Record<ChangeOrderInvoiceStatus, string> = {
  not_invoiced: 'Not Invoiced',
  invoiced: 'Invoiced',
  paid: 'Paid',
  cancelled: 'Cancelled',
  not_billable: 'Not Billable',
};

export const CO_INVOICE_STATUS_STYLES: Record<ChangeOrderInvoiceStatus, { bg: string; text: string }> = {
  not_invoiced: { bg: '#F3F4F6', text: '#6B7280' },
  invoiced: { bg: '#FEF0E7', text: '#E8621A' },
  paid: { bg: '#F0FDF4', text: '#059669' },
  cancelled: { bg: '#FEF2F2', text: '#EF4444' },
  not_billable: { bg: '#F9FAFB', text: '#9CA3AF' },
};

export const CO_INVOICE_FILE_TYPE = 'co_invoice';

export interface ChangeOrderInvoiceFields {
  invoice_status: ChangeOrderInvoiceStatus | string | null;
  invoice_url: string | null;
  invoice_file_id: string | null;
  invoiced_at: string | null;
  paid_at: string | null;
}

export function normalizeInvoiceStatus(status: string | null | undefined): ChangeOrderInvoiceStatus {
  if (status && CO_INVOICE_STATUSES.includes(status as ChangeOrderInvoiceStatus)) {
    return status as ChangeOrderInvoiceStatus;
  }
  return 'not_invoiced';
}

export function getInvoiceStatusLabel(status: string | null | undefined): string {
  const normalized = normalizeInvoiceStatus(status);
  return CO_INVOICE_STATUS_LABELS[normalized];
}

export function getInvoiceStatusStyle(status: string | null | undefined) {
  const normalized = normalizeInvoiceStatus(status);
  return CO_INVOICE_STATUS_STYLES[normalized];
}

export function buildInvoicePayload(
  invoiceStatus: ChangeOrderInvoiceStatus,
  invoiceUrl: string,
  invoiceFileId: string | null,
  previous: Pick<ChangeOrderInvoiceFields, 'invoice_status' | 'invoiced_at' | 'paid_at'>
): Record<string, unknown> {
  const now = new Date().toISOString();
  const payload: Record<string, unknown> = {
    invoice_status: invoiceStatus,
    invoice_url: invoiceUrl.trim() || null,
    invoice_file_id: invoiceFileId || null,
  };

  if (invoiceStatus === 'invoiced' && previous.invoice_status !== 'invoiced') {
    payload.invoiced_at = now;
  } else if (invoiceStatus === 'invoiced' && previous.invoiced_at) {
    payload.invoiced_at = previous.invoiced_at;
  } else if (invoiceStatus !== 'invoiced' && invoiceStatus !== 'paid') {
    payload.invoiced_at = null;
  }

  if (invoiceStatus === 'paid') {
    payload.paid_at = previous.paid_at && previous.invoice_status === 'paid' ? previous.paid_at : now;
    if (!previous.invoiced_at && previous.invoice_status !== 'invoiced') {
      payload.invoiced_at = previous.invoiced_at || now;
    }
  } else if (previous.invoice_status === 'paid') {
    payload.paid_at = null;
  }

  return payload;
}

export const CO_INVOICE_MIGRATION_HINT =
  'Invoice fields require the CO invoice SQL migration: supabase/migrations/20260706120000_change_order_invoice_fields.sql';

export function isInvoiceColumnError(message: string): boolean {
  return /invoice|schema cache|column|does not exist/i.test(message);
}

export function hasInvoiceFieldChanges(
  next: {
    invoiceStatus: ChangeOrderInvoiceStatus;
    invoiceUrl: string;
    invoiceFileId: string | null;
  },
  previous: Pick<ChangeOrderInvoiceFields, 'invoice_status' | 'invoice_url' | 'invoice_file_id'>
): boolean {
  return (
    next.invoiceStatus !== normalizeInvoiceStatus(previous.invoice_status) ||
    next.invoiceUrl.trim() !== (previous.invoice_url || '').trim() ||
    (next.invoiceFileId || null) !== (previous.invoice_file_id || null)
  );
}

export interface UploadedInvoiceFileRecord {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
}

export async function uploadChangeOrderInvoiceFile(params: {
  file: File;
  projectId: string;
  changeOrderId: string;
  userId: string;
}): Promise<{ record: UploadedInvoiceFileRecord | null; error?: string }> {
  const ext = params.file.name.split('.').pop()?.toLowerCase() || 'pdf';
  const safeName = params.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const ts = Date.now();
  const path = `project-files/${params.projectId}/${ts}_${safeName}`;

  const { error: storageError } = await supabase.storage
    .from('project-files')
    .upload(path, params.file, { upsert: false });

  if (storageError) {
    return { record: null, error: `Storage upload failed: ${storageError.message}` };
  }

  const { data: urlData } = supabase.storage.from('project-files').getPublicUrl(path);
  const publicUrl = urlData?.publicUrl ?? null;
  if (!publicUrl) {
    return { record: null, error: 'Storage upload succeeded but no public URL was returned' };
  }

  const { data, error } = await supabase
    .from('jphph_project_files')
    .insert({
      project_id: params.projectId,
      file_name: params.file.name,
      file_url: publicUrl,
      storage_bucket: 'project-files',
      storage_path: path,
      file_size: String(params.file.size),
      file_type: ext,
      note: 'Change order invoice',
      uploaded_by: params.userId,
      is_visible: true,
      is_client_visible: false,
      is_deleted: false,
      photo_type: CO_INVOICE_FILE_TYPE,
      uploaded_context: 'change_order',
      change_order_id: params.changeOrderId,
    })
    .select('id, file_name, file_url, file_type, storage_bucket, storage_path')
    .maybeSingle();

  if (error || !data) {
    return { record: null, error: error?.message || 'Failed to save invoice file record' };
  }

  return {
    record: {
      id: (data as { id: string }).id,
      file_name: (data as { file_name: string }).file_name,
      file_url: (data as { file_url: string }).file_url,
      file_type: (data as { file_type: string | null }).file_type,
      storage_bucket: (data as { storage_bucket: string | null }).storage_bucket,
      storage_path: (data as { storage_path: string | null }).storage_path,
    },
  };
}
